// Small reusable widgets used across TurnoverDetail sections.
// Grouped here to avoid a proliferation of tiny single-component files.

import { useState, useRef } from "react";
import { STATUS_CONFIG } from "./shared";

// ── StatusPill ─────────────────────────────────────────────────────────────────
export function StatusPill({ status }) {
  const cfg = STATUS_CONFIG[status] || { bg: "#f3f4f6", color: "#374151", dot: "#9ca3af" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      fontSize: 11, fontWeight: 600, padding: "3px 9px",
      borderRadius: 100, background: cfg.bg, color: cfg.color,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.dot, flexShrink: 0 }} />
      {status}
    </span>
  );
}

// ── SectionIcon ────────────────────────────────────────────────────────────────
export function SectionIcon({ section }) {
  if (!section.IsApplicable) return <span style={{ fontSize: 13 }}>➖</span>;
  if (section.IsComplete)    return <span style={{ fontSize: 13, color: "#10b981" }}>✅</span>;
  return <span style={{ fontSize: 13, color: "#d1d5db" }}>⬜</span>;
}

// ── DropZone ───────────────────────────────────────────────────────────────────
export function DropZone({ onFile, uploading, multiple = false }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;
    if (multiple) { files.forEach(onFile); } else { onFile(files[0]); }
  }

  function handleChange(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (multiple) { files.forEach(onFile); } else { onFile(files[0]); }
    e.target.value = "";
  }

  return (
    <div
      onClick={() => !uploading && inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      style={{
        border: `2px dashed ${dragging ? "var(--cs-blue)" : "var(--cs-border)"}`,
        borderRadius: 10, padding: "28px 20px",
        textAlign: "center", cursor: uploading ? "default" : "pointer",
        background: dragging ? "#eff6ff" : "#fafbfc",
        transition: "all 0.15s",
      }}
    >
      {uploading ? (
        <div style={{ color: "var(--text3)", fontSize: 13 }}>
          <div className="spinner spinner-dark" style={{ display: "inline-block", marginBottom: 8 }} />
          <div>Uploading to SharePoint…</div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 28, marginBottom: 6 }}>📎</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text2)", marginBottom: 3 }}>
            {multiple ? "Drop files here or click to browse (multiple allowed)" : "Drop file here or click to browse"}
          </div>
          <div style={{ fontSize: 11, color: "var(--text3)" }}>PDF, DOCX, XLSX, DWG, and more</div>
        </>
      )}
      <input ref={inputRef} type="file" multiple={multiple} style={{ display: "none" }} onChange={handleChange} />
    </div>
  );
}

// ── KV — compact label+value for header info grid ─────────────────────────────
export function KV({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
      <span style={{ fontSize: 10, color: "var(--text3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", flexShrink: 0 }}>
        {label}
      </span>
      <span style={{ fontSize: 12, color: "var(--text2)" }}>{value}</span>
    </div>
  );
}

// ── Cover-sheet form sub-components ───────────────────────────────────────────
export function SectionBlock({ title, icon, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: "var(--text3)",
        textTransform: "uppercase", letterSpacing: "0.06em",
        marginBottom: 10, display: "flex", alignItems: "center", gap: 6,
        borderBottom: "1px solid var(--cs-border)", paddingBottom: 6,
      }}>
        {icon && <span>{icon}</span>} {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

export function ReadRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: "flex", gap: 12, marginBottom: 6, alignItems: "baseline" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", width: 160, flexShrink: 0 }}>{label}</div>
      <div style={{ fontSize: 13, color: "var(--text)" }}>{value}</div>
    </div>
  );
}

export function EditRow({ label, value, onChange, placeholder, type = "text", multiline = false, required = false }) {
  const isEmpty = required && (!value || value === "");
  const inputStyle = {
    width: "100%", padding: "6px 10px", fontSize: 13,
    border: isEmpty ? "1px solid #F59E0B" : "1px solid var(--cs-border)",
    borderRadius: 6,
    background: isEmpty ? "#FFFBEB" : "var(--surface)",
    color: "var(--text)",
    boxSizing: "border-box", fontFamily: "inherit",
    transition: "background 0.2s, border-color 0.2s",
  };
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: isEmpty ? "#92400E" : "var(--text3)", display: "block", marginBottom: 4 }}>
        {label}{required && <span style={{ color: "#F59E0B", marginLeft: 3 }}>●</span>}
      </label>
      {multiline ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3}
          style={{ ...inputStyle, resize: "vertical" }} />
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={inputStyle} />
      )}
    </div>
  );
}

export function SelectRow({ label, value, onChange, options, loading = false, required = false, fetchFailed = false }) {
  const isEmpty = required && (!value || value === "");
  const fieldStyle = {
    // maxWidth: keeps the native list from spanning the whole panel on a wide window.
    width: "100%", maxWidth: 420, padding: "6px 10px", fontSize: 13,
    border: isEmpty ? "1px solid #F59E0B" : "1px solid var(--cs-border)",
    borderRadius: 6,
    background: isEmpty ? "#FFFBEB" : "var(--surface)",
    color: value ? "var(--text)" : "var(--text3)",
    boxSizing: "border-box", fontFamily: "inherit",
    transition: "background 0.2s, border-color 0.2s",
  };
  const hasMatch = options.some(o => o.label === value);
  const directoryDown = options.length === 0 && !loading;

  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: isEmpty ? "#92400E" : "var(--text3)", display: "block", marginBottom: 4 }}>
        {label}{required && <span style={{ color: "#F59E0B", marginLeft: 3 }}>●</span>}
      </label>
      {(fetchFailed && directoryDown) ? (
        <div>
          <input type="text" value={value || ""} onChange={e => onChange(e.target.value)}
            placeholder="Type PM name…" style={{ ...fieldStyle, color: "var(--text)", cursor: "text" }} />
          <div style={{ fontSize: 10, color: "#92400E", marginTop: 3 }}>
            Directory unavailable — type name manually. (Admin: grant User.ReadBasic.All consent in Azure AD)
          </div>
        </div>
      ) : (
        <select value={value || ""} onChange={e => onChange(e.target.value)}
          style={{ ...fieldStyle, cursor: "pointer" }} disabled={loading}>
          <option value="">{loading ? "Loading directory…" : "— Select PM —"}</option>
          {!hasMatch && value && <option value={value}>{value}</option>}
          {options.map(o => <option key={o.email || o.label} value={o.label}>{o.label}</option>)}
        </select>
      )}
    </div>
  );
}

// ── AssigneeControl ────────────────────────────────────────────────────────────
export function AssigneeControl({ section, directoryUsers, directoryFailed, onReassign, onReset }) {
  const [editing, setEditing] = useState(false);
  const isOverride = section.AssignmentSource === "override";
  const name = section.AssignedTo || "Unassigned";

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        title="Click to reassign"
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "5px 10px", fontSize: 12, fontWeight: 600,
          border: "1px solid var(--cs-border)", borderRadius: 6,
          background: isOverride ? "#eef2ff" : "transparent",
          color: isOverride ? "#4338ca" : "var(--text2)",
          cursor: "pointer",
        }}
      >
        {name}
        {isOverride && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.04em" }}>OVERRIDE</span>}
      </button>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {directoryFailed || directoryUsers.length === 0 ? (
        <input
          type="text"
          defaultValue={section.AssignedTo || ""}
          placeholder="Type name…"
          onBlur={e => { onReassign(section.SectionID, e.target.value, ""); setEditing(false); }}
          style={{ padding: "5px 8px", fontSize: 12, border: "1px solid var(--cs-border)", borderRadius: 6 }}
          autoFocus
        />
      ) : (
        <select
          defaultValue={section.AssignedTo || ""}
          onChange={e => {
            const picked = directoryUsers.find(u => u.label === e.target.value);
            onReassign(section.SectionID, picked?.label || "", picked?.email || "");
            setEditing(false);
          }}
          onBlur={() => setEditing(false)}
          style={{ padding: "5px 8px", fontSize: 12, border: "1px solid var(--cs-border)", borderRadius: 6 }}
          autoFocus
        >
          <option value="">— Select —</option>
          {section.AssignedTo && !directoryUsers.some(u => u.label === section.AssignedTo) && (
            <option value={section.AssignedTo}>{section.AssignedTo} (not in directory)</option>
          )}
          {directoryUsers.map(u => <option key={u.email} value={u.label}>{u.label}</option>)}
        </select>
      )}
      {isOverride && (
        <button
          onClick={() => { onReset(section.SectionID); setEditing(false); }}
          title="Reset to role default"
          style={{ padding: "5px 8px", fontSize: 11, fontWeight: 600, border: "1px solid var(--cs-border)", borderRadius: 6, background: "transparent", color: "var(--text3)", cursor: "pointer" }}
        >
          Reset
        </button>
      )}
    </div>
  );
}
